// TABLOUL VERIFICĂRILOR STRUCTURII: forma în care îl dă serverul și numărătorile
// ecranului.
//
// Verdictele stau în rezultatele pașilor (pe element, fundația, driftul,
// modurile, echilibrul, amplasarea, picatele Pasului 19), câte un artefact de
// megaocteți pe pas. Le adună serverul (`structure/tablou_verificari.py`), iar
// ecranul primește:
//
//   * SUMARUL — celulele: câte rânduri are fiecare combinație (grup, tip,
//     tematică, verdict, direcție). Cadranul, cele trei fațete și capetele
//     cardurilor se numără din ele, cu filtrele ecranului puse;
//   * RÂNDURILE unui card, pe pagini, abia când i se deschide.

import { CatalogVerificari, categoriaDin } from "./tipuri";
import { ElementIndexat, VerdictElement, VerificareElement } from "./elemente";
import { t } from "../i18n";
import type { Cheie } from "../i18n";
import { Ancora } from "./legaturi";

/** Verdictul unui rând. Pe lângă cele trei ale elementelor:
 *  `neaplicabil` — verificarea nu se cere (scutire din normativ, cale de
 *  presiune neguvernantă, direcție rotită la T7): nu e nici trecută, nici
 *  picată, iar numărată ca trecută ar lingușit raportul;
 *  `informativ` — valoarea se raportează, dar nu e un verdict (tasarea
 *  relativă între axe, G11). */
export type Verdict = VerdictElement | "neaplicabil" | "informativ";

/** Un rând al tabloului, cum îl compune serverul. */
export type RandVerificare = {
  element: string | null;
  tipElement: ElementIndexat["tip"] | null;
  nivel: string | number | null;
  pas: string;
  nume: string;
  grupa?: string | null;
  /** categoria din catalog, când o știm */
  tip: string | null;
  /** titlul canonic al categoriei, când vine din Pasul 19 */
  eticheta: string | null;
  /** cheia tematicii (catalog_verificari.TEMATICI) */
  tematica: string | null;
  /** DIRECȚIA pe care s-a măsurat («X», «Y», «45°»), când verificarea se face
   *  pe fiecare direcție în parte; tabloul o arată pe coloana ei */
  directie: string | null;
  verdict: Verdict;
  RE: number | null;
  v: VerificareElement;
  /** codul verificării de fundație (G1…G13, S3…S7) */
  cod?: string | null;
  /** de unde vine: indexul de elemente, verdictele globale ale unui pas sau
   *  o picată a Pasului 19 pe care n-a văzut-o nimeni altcineva */
  sursa: "element" | "global" | "p17";
  ancora?: Ancora | null;
  descriere?: string | null;
};

/** O celulă a sumarului: rândurile cu același (grup, tip, tematică, verdict,
 *  direcție). `eticheta`, `nume` și `pas` sunt ale primului ei rând, `E` și
 *  `R` ale celui mai strâns (ordinea cardului). */
export type CelulaVerificari = {
  grup: string;
  tip: string | null;
  tematica: string | null;
  verdict: Verdict;
  directie: string | null;
  n: number;
  eticheta: string | null;
  nume: string | null;
  pas: string;
  E: string | number | null;
  R: string | number | null;
};

/** Rândul de trasabilitate al Pasului 19: ce pași au fost citiți și câte
 *  verificări a dat fiecare. */
export type AcoperireVerificari = {
  pas: string;
  nume: string;
  subiect?: string;
  citit: boolean;
  nr_verificari: number;
  nr_picate?: number;
};

export type SumarVerificari = {
  total: number;
  celule: CelulaVerificari[];
  acoperire: AcoperireVerificari[];
};

export type PaginaVerificari = {
  total: number;
  inceput: number;
  randuri: RandVerificare[];
};

/** Câte rânduri are o pagină a unui card, la deschidere și la «încă». */
export const PAGINA_CARD = 40;
export const PAGINA_INCA = 200;

/** Treapta de SEVERITATE a unei verificări — cât de departe se propagă o
 *  picată din categoria ei. Vine din catalog (`prioritate_tip` pe server);
 *  fără catalog nu se poate ști, deci `null`. */
export type Severitate = "CRITICAL" | "HIGH" | "MED" | "LOW";

/** Treptele GLOBALE: o picată de aici schimbă eforturile din tot ce urmează,
 *  deci restul verdictelor sunt calculate pe cifre care se vor schimba.
 *  Aceeași mulțime ca `catalog_verificari.PRIORITATI_GLOBALE` pe server. */
const SEVERITATI_GLOBALE: ReadonlySet<Severitate> = new Set(["CRITICAL", "HIGH"]);

/** Câte dintre picate sunt GLOBALE (CRITICAL sau HIGH din catalog: drift,
 *  bici, moduri, validitatea analizei). Una MED sau LOW se repară pe
 *  elementul ei și nu atinge nimic altceva. De asta contează la culoarea
 *  cadranului. */
export function picateGlobale(
  celule: CelulaVerificari[],
  catalog: CatalogVerificari | null
): number {
  let n = 0;
  for (const x of celule) {
    if (x.verdict !== "neconform") continue;
    const s = severitateaDin(catalog, x.tip);
    if (s && SEVERITATI_GLOBALE.has(s)) n += x.n;
  }
  return n;
}

/** Cele patru trepte, în ordinea reparației.
 *
 *  CRITICAL sunt deplasările pe care structuristul le cere trecute fără
 *  excepție (driftul și efectul de bici): o soluție care le pică nu poate
 *  bate una care le trece. HIGH e restul globalelor.
 *
 *  NU au „ton". Severitatea nu spune ce a ieșit din calcul, ci cât de departe
 *  se propagă o picată din categoria asta; își ia alt alfabet decât verdictul
 *  (badge cu colțuri drepte, cerneală neutră, un contor de patru bare — v.
 *  `Treapta` în VerificariPage), iar `rang` e tot ce-i trebuie de aici. */
export const SEVERITATI: {
  cod: Severitate;
  /** 1 se repară prima; contorul de bare se desenează din el */
  rang: 1 | 2 | 3 | 4;
  eticheta: Cheie;
  ce: Cheie;
}[] = [
  {
    cod: "CRITICAL",
    rang: 1,
    eticheta: "struct.sev.critica",
    ce: "struct.sev.critica_ce",
  },
  {
    cod: "HIGH",
    rang: 2,
    eticheta: "struct.sev.globala",
    ce: "struct.sev.globala_ce",
  },
  {
    cod: "MED",
    rang: 3,
    eticheta: "struct.sev.importanta",
    ce: "struct.sev.importanta_ce",
  },
  {
    cod: "LOW",
    rang: 4,
    eticheta: "struct.sev.locala",
    ce: "struct.sev.locala_ce",
  },
];

/** Numărul de bare al contorului = numărul de trepte; `rang` 1 le are pline
 *  pe toate. */
export const NR_TREPTE = SEVERITATI.length;

export function severitateaDin(
  catalog: CatalogVerificari | null,
  tip: string | null | undefined
): Severitate | null {
  const p = categoriaDin(catalog, tip ?? "")?.prioritate;
  return SEVERITATI.some((s) => s.cod === p) ? (p as Severitate) : null;
}

/** Câte rânduri are fiecare treaptă de severitate — pentru pastilele de
 *  filtrare. Se numără TOATE rândurile, nu doar picatele: filtrul e pe
 *  categorie, nu pe verdict. */
export function numaraSeveritati(
  celule: CelulaVerificari[],
  catalog: CatalogVerificari | null
): Record<Severitate, number> {
  const out: Record<Severitate, number> = { CRITICAL: 0, HIGH: 0, MED: 0, LOW: 0 };
  for (const x of celule) {
    const s = severitateaDin(catalog, x.tip);
    if (s) out[s] += x.n;
  }
  return out;
}

/** Câte rânduri are fiecare verdict, și numitorul cadranului. */
export function numaraVerdicte(celule: CelulaVerificari[]) {
  const c = {
    neconform: 0,
    la_limita: 0,
    conform: 0,
    neaplicabil: 0,
    informativ: 0,
    total: 0,
    // numitorul rotiței: ce s-a putut MĂSURA (trece + la limită + pică).
    // Neaplicabilele și informativele nu sunt verdicte, iar numărate ca
    // reușite ar da un procent care crește cu fiecare verificare pe care
    // lanțul nu o cere — exact felul în care un raport se lingușește.
    masurate: 0,
  };
  for (const x of celule) {
    c[x.verdict] += x.n;
    c.total += x.n;
  }
  c.masurate = c.conform + c.la_limita + c.neconform;
  return c;
}

export type GrupTematica = {
  cheie: string;
  eticheta: string;
  ce: string;
  pasi: string[];
  total: number;
  neconform: number;
  la_limita: number;
  conform: number;
  neaplicabil: number;
  informativ: number;
};

/** Tematicile CU rânduri, în ordinea catalogului (care e ordinea lanțului:
 *  întâi ce invalidează tot ce urmează, apoi capacitatea elementelor). */
export function tematiciVerificari(
  celule: CelulaVerificari[],
  catalog: CatalogVerificari | null
): GrupTematica[] {
  const def = catalog?.tematici ?? [];
  const goale = () => ({
    total: 0, neconform: 0, la_limita: 0, conform: 0,
    neaplicabil: 0, informativ: 0, pasi: new Set<string>(),
  });
  const m = new Map<string, ReturnType<typeof goale>>();
  for (const x of celule) {
    const k = x.tematica ?? "";
    const g = m.get(k) ?? goale();
    g.total += x.n;
    g[x.verdict] += x.n;
    if (x.pas) g.pasi.add(x.pas);
    m.set(k, g);
  }
  const out: GrupTematica[] = [];
  for (const t of def) {
    const g = m.get(t.cheie);
    if (!g) continue;
    out.push({
      cheie: t.cheie, eticheta: t.eticheta, ce: t.ce,
      pasi: [...g.pasi],
      total: g.total, neconform: g.neconform, la_limita: g.la_limita,
      conform: g.conform, neaplicabil: g.neaplicabil,
      informativ: g.informativ,
    });
  }
  // rândurile fără tematică (categorie nouă pe server, catalog vechi în
  // browser) NU se pierd: capătă o grupă proprie, vizibilă ca atare
  const rest = m.get("");
  if (rest)
    out.push({
      cheie: "", eticheta: t("struct.tem.fara"),
      ce: t("struct.tem.fara_ce"),
      pasi: [...rest.pasi],
      total: rest.total, neconform: rest.neconform, la_limita: rest.la_limita,
      conform: rest.conform, neaplicabil: rest.neaplicabil,
      informativ: rest.informativ,
    });
  return out;
}

export type GrupTip = {
  /** cheia cardului (`grup` al celulelor): categoria, altfel eticheta,
   *  altfel numele */
  tip: string;
  eticheta: string;
  pas: string;
  total: number;
  neconform: number;
  la_limita: number;
  conform: number;
  neaplicabil: number;
  informativ: number;
  celule: CelulaVerificari[];
};

/** TIPURILE de verificare dintr-un set de celule (nivelul de sub tematică),
 *  ordonate după picate, apoi după cele la limită, apoi după volum. Capul
 *  cardului se ia de la prima celulă, adică de la primul rând rămas. */
export function tipuriVerificari(
  celule: CelulaVerificari[],
  catalog: CatalogVerificari | null
): GrupTip[] {
  const m = new Map<string, GrupTip>();
  for (const x of celule) {
    const tip = x.grup;
    const g =
      m.get(tip) ??
      {
        tip,
        eticheta:
          x.eticheta ??
          catalog?.categorii?.[tip]?.eticheta ??
          (catalog && x.tip ? catalog.categorii[x.tip]?.eticheta : null) ??
          x.nume ?? tip,
        pas: x.pas,
        total: 0, neconform: 0, la_limita: 0, conform: 0,
        neaplicabil: 0, informativ: 0,
        celule: [] as CelulaVerificari[],
      };
    g.total += x.n;
    g[x.verdict] += x.n;
    g.celule.push(x);
    m.set(tip, g);
  }
  return [...m.values()].sort(
    (a, b) =>
      b.neconform - a.neconform ||
      b.la_limita - a.la_limita ||
      b.total - a.total ||
      a.eticheta.localeCompare(b.eticheta)
  );
}

/** Ordinea gravității: verdictul unei direcții e cel mai grav al ei. */
export const ORDINEA_VERDICTELOR: Verdict[] = [
  "neconform", "la_limita", "conform", "neaplicabil", "informativ",
];
